import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import ArticleCard from "@/components/feature/ArticleCard";
import { usePageSeo } from "@/hooks/usePageSeo";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);

  usePageSeo({
    title: query ? `搜索「${query}」的结果` : "搜索爆料",
    description: query
      ? `爆料专区中与「${query}」相关的内容整理，涵盖今日热点、新鲜事与吃瓜合集。`
      : "在爆料专区搜索你关心的今日热点、明星八卦、社会趣事与网络热闻。",
    keywords: "爆料专区搜索,今日热点,吃瓜合集",
    path: "/search",
    type: "website",
    noindex: true,
  });

  useEffect(() => {
    setInput(query);
  }, [query]);

  const results = useMemo(() => {
    const kw = query.trim();
    if (!kw) {
      return [...articles].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
    return articles
      .filter(
        (item) =>
          item.title.includes(kw) ||
          item.excerpt.includes(kw) ||
          item.categoryName.includes(kw) ||
          item.tags.some((tag) => tag.includes(kw)),
      )
      .sort((a, b) => b.views - a.views);
  }, [query]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = input.trim();
    if (q) {
      setSearchParams({ q });
    } else {
      setSearchParams({});
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
            <nav aria-label="面包屑" className="flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800">搜索爆料</span>
            </nav>
            <h1 className="mt-4 font-heading text-2xl tracking-wide text-foreground-950 md:text-4xl">
              {query ? `搜索：${query}` : "搜索全部爆料"}
            </h1>
            <form onSubmit={submit} className="relative mt-5 flex max-w-xl gap-3" role="search">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center justify-center text-foreground-400">
                  <i className="ri-search-line text-sm"></i>
                </span>
                <input
                  type="search"
                  name="q"
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  placeholder="输入关键词，例如：古装剧、热榜、演唱会"
                  className="h-11 w-full rounded-md border border-background-200 bg-background-50 pl-9 pr-3 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <button
                type="submit"
                className="flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
            <div className="mt-4 flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.slug}
                  type="button"
                  onClick={() => setSearchParams({ q: cat.name })}
                  className="cursor-pointer rounded-full bg-background-50 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
          <p className="text-sm text-foreground-600">
            {query ? (
              <>
                找到 <span className="font-medium text-primary-600">{results.length}</span> 条与「{query}」相关的爆料
              </>
            ) : (
              <>共 {results.length} 条爆料，按发布时间为你排序</>
            )}
          </p>

          {results.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="mt-16 flex flex-col items-center text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <p className="mt-5 text-sm text-foreground-600">没有找到相关爆料，换个关键词试试吧。</p>
              <Link
                to="/"
                className="mt-6 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                回到首页看今日热榜
              </Link>
            </div>
          )}
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}